import { useSearch } from "@tanstack/react-router";
import { Button } from "antd";
import { useDestroyOfferCustomField } from "api/TenantSlug/OfferCustomFields/hooks.ts";
import {
    AddCustomInformationFieldForm
} from "components/Forms/AddCustomInformationFieldForm/AddCustomInformationFieldForm.tsx";
import { DeleteModal } from "components/other/DeleteModal/DeleteModal.tsx";
import { Drawer } from "components/UI/Drawer";
import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible.tsx";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent.tsx";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle.tsx";
import { useDrawer } from "hooks/useDrawer.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, useState } from "react";
import { toast } from "react-toastify";
import { DrawerId, FormId } from "types/models.ts";

interface Props {
    offerId: string;
    readOnly?: boolean;
    onSubmit?: () => void;
}

export const AddCustomInformationFieldDrawer: FC<Props> = ({ offerId, readOnly, onSubmit }) => {
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const drawerUtils = useDrawer();
    const { t } = usePaldockTranslation("drawers.add-custom-information-field");
    const { t: tC } = usePaldockTranslation("common");
    const search = useSearch({ strict: false }) as { customFieldId?: string };
    const customFieldId = search.customFieldId;
    const searchParamsToClean = ["customFieldId"];

    const { mutate: destroyField, isPending } = useDestroyOfferCustomField(offerId, customFieldId ?? "", {});

    const handleClose = () => {
        setDeleteModalOpen(false);
        drawerUtils.hide(DrawerId.AddCustomInformationField, searchParamsToClean);
    };

    const handleSubmit = () => {
        onSubmit?.();
        handleClose();
    };

    const onDelete = () => {
        if (!customFieldId) {
            return;
        }
        destroyField(void null, {
            onSuccess: () => {
                setDeleteModalOpen(false);
                toast.success(t("messages.delete_success"));
                handleClose();
                onSubmit?.();
            },
            onError: () => {
                toast.error(t("messages.delete_error"));
            },
        });
    };

    return (
        <>
            <Drawer
                drawerId={DrawerId.AddCustomInformationField}
                title={<DrawerTitle mainTitle={t("title")} />}
                searchParamsToClean={searchParamsToClean}
                onBeforeClose={() => {
                    setDeleteModalOpen(false);
                    return true;
                }}
                extraButtons={
                    <>
                        {customFieldId && !readOnly && (
                            <Button
                                size={"large"}
                                danger
                                type={"primary"}
                                disabled={isPending}
                                onClick={() => setDeleteModalOpen(true)}
                            >
                                {tC("delete")}
                            </Button>
                        )}
                        {!readOnly && (
                            <Button
                                size={"large"}
                                htmlType="submit"
                                type={"primary"}
                                form={FormId.AddCustomInformationFieldForm}
                            >
                                {tC("save")}
                            </Button>
                        )}
                    </>
                }
                destroyOnClose={true}>
                <DrawerContent>
                    <DrawerCollapsible title={t('section_title')}>
                        <AddCustomInformationFieldForm offerId={offerId} customFieldId={customFieldId} readOnly={readOnly} onSubmit={handleSubmit} />
                    </DrawerCollapsible>
                </DrawerContent>
            </Drawer>
            <DeleteModal
                open={deleteModalOpen}
                title={t("delete_modal_title")}
                onCancel={() => setDeleteModalOpen(false)}
                onConfirm={onDelete}
                loading={isPending}
            />
        </>
    )
}
